import React, { Component, useEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { surfaceFor } from '../../data/designVariants';
import { useDesignVariant, type DesignPreview } from '../../hooks/useDesignVariant';
import type { DesignId, SurfaceKey } from '../../types/experience';

/**
 * Presents one surface through whichever design is currently configured.
 *
 * This is the only component that swaps a design, and it is shared by every
 * surface — the homepage and housing do not each have their own swapper, own
 * crossfade or own error handling. It mounts ONE design at a time, so the
 * other's photography and animation cost nothing while it is not showing.
 *
 * The handover is a crossfade with the frame height held, so the page neither
 * flashes nor collapses under the reader. Nothing on screen names a design or
 * announces the change.
 */
interface Props {
  surface: SurfaceKey;
  /** Renders a design. Called for exactly one design at a time. */
  render: (design: DesignId) => ReactNode;
  /**
   * True where the two designs are structured so differently that a scroll
   * position in one is meaningless in the other.
   */
  scrollToTopOnChange?: boolean;
  /** A review-only override. Never shown to visitors. */
  developerControl?: boolean;
}

/**
 * A design that throws must never leave anyone on a broken page, so a render
 * error falls back to the surface's fallback design.
 */
interface BoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  design: DesignId;
  onFailure: (design: DesignId) => void;
}
class DesignBoundary extends Component<BoundaryProps, {
  failed: boolean;
}> {
  state = {
    failed: false
  };
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  componentDidCatch() {
    this.props.onFailure(this.props.design);
  }
  componentDidUpdate(previous: BoundaryProps) {
    // A different design deserves its own attempt.
    if (previous.design !== this.props.design && this.state.failed) {
      this.setState({
        failed: false
      });
    }
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/** Warms the design that is coming, well before it is needed. */
function usePreload(assets: readonly string[]) {
  useEffect(() => {
    if (assets.length === 0) return undefined;
    const timer = window.setTimeout(() => {
      assets.forEach(src => {
        const image = new Image();
        image.decoding = 'async';
        image.src = src;
      });
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [assets]);
}
export function DesignVariantSwitch({
  surface,
  render,
  scrollToTopOnChange = false,
  developerControl = false
}: Props) {
  const {
    design,
    upcoming,
    config,
    preview,
    setPreview
  } = useDesignVariant(surface);
  const descriptor = surfaceFor(surface);
  const reduce = useReducedMotion();
  const frame = useRef<HTMLDivElement>(null);
  const upcomingAssets = descriptor.designs.find(entry => entry.id === upcoming)?.criticalAssets ?? [];
  usePreload(upcomingAssets);

  // Hold the outgoing height for the length of the crossfade so the document
  // does not jump while one design leaves and the other arrives.
  useEffect(() => {
    const node = frame.current;
    if (!node) return undefined;
    node.style.minHeight = `${node.offsetHeight}px`;
    const release = window.setTimeout(() => {
      node.style.minHeight = '';
    }, 700);
    return () => window.clearTimeout(release);
  }, [design]);
  useEffect(() => {
    if (!scrollToTopOnChange) return;
    window.scrollTo({
      top: 0,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [design, reduce, scrollToTopOnChange]);
  const fallback = design === config.fallbackDesign ? <RecoveryNotice path={descriptor.path} data-id="element-1738" data-mp="muvyz9i02luh" /> : <>{render(config.fallbackDesign)}</>;
  return <>
      <div ref={frame} data-campuzon-design={design.toLowerCase()} data-id="element-1739" data-mp="1tcarxtl9mqb9">
        <AnimatePresence mode="wait" initial={false} data-id="element-1740" data-mp="1y7yfk4k3nben">
          <motion.div key={design} initial={reduce ? false : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={reduce ? {
          opacity: 1
        } : {
          opacity: 0
        }} transition={{
          duration: reduce ? 0 : 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1741" data-mp="1vumgf2hdngv4">
            <DesignBoundary design={design} fallback={fallback} onFailure={() => undefined} data-id="element-1742" data-mp="20tpdo0r48isj">
              {render(design)}
            </DesignBoundary>
          </motion.div>
        </AnimatePresence>
      </div>

      {developerControl && <ReviewControl preview={preview} onChange={setPreview} data-id="element-1743" data-mp="2aq2hvskaef1j" />}
    </>;
}

/** Shown only if the fallback design itself fails — never a blank page. */
function RecoveryNotice({
  path
}: {
  path: string;
}) {
  return <div className="mx-auto max-w-[42ch] px-6 py-20 text-center" data-id="element-1744" data-mp="2yh7y48u539cd">
      <h1 className="text-h3 font-semibold text-ink" data-id="element-1745" data-mp="2yhptz4cw3ljr">This page could not be displayed</h1>
      <p className="mt-3 text-body text-ink-muted" data-id="element-1746" data-mp="2tk7r9vpgu2k7">
        Nothing on your account has changed. Reloading usually resolves it.
      </p>
      <a href={path} className="mt-6 inline-flex min-h-[44px] items-center rounded-xl bg-brand-700 px-5 text-body font-semibold text-white hover:text-white" data-id="element-1747" data-mp="3h5rxrbul5tuy">
        Try again
      </a>
    </div>;
}
function ReviewControl({
  preview,
  onChange
}: {
  preview: DesignPreview;
  onChange: (preview: DesignPreview) => void;
}) {
  const options: {
    id: DesignPreview;
    label: string;
  }[] = [{
    id: 'auto',
    label: 'Configured'
  }, {
    id: 'DESIGN_1',
    label: 'Design 1'
  }, {
    id: 'DESIGN_2',
    label: 'Design 2'
  }];
  return <div className="fixed bottom-4 left-4 z-[500] rounded-xl border border-line bg-surface/95 p-2 shadow-md" data-id="element-1748" data-mp="2kh2f3jb4wx7x">
      <p className="px-1 pb-1 text-caption text-ink-subtle" data-id="element-1749" data-mp="2peu3i3ckm17d">Review control — not shown to visitors</p>
      <div className="flex gap-1" data-id="element-1750" data-mp="2ajehmlm59bm2">
        {options.map(option => <button key={option.id} type="button" onClick={() => onChange(option.id)} aria-pressed={preview === option.id} className={`min-h-[36px] rounded-lg px-3 text-caption font-medium transition-colors duration-150 ease-out ${preview === option.id ? 'bg-brand-700 text-white hover:text-white' : 'text-ink hover:bg-canvas'}`} data-id="element-1751" data-mp="2ff39s3vu3q8c">
            {option.label}
          </button>)}
      </div>
    </div>;
}